import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Check, ChevronDown } from "lucide-react";

export type SelectOption = { value: string; label: string; hint?: string; disabled?: boolean };

/**
 * 唯一正式下拉选择器。台账筛选、表单下拉与排序统一使用本组件，
 * 禁止原生 <select> 或领域自建下拉。支持键盘操作（方向键、Home/End、Enter、Escape），
 * 触发器目标至少 44px。
 */
export function V3Select({ value, onChange, options, placeholder = "请选择", disabled = false, ariaLabel }: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find((option) => option.value === value) ?? null;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const list = rootRef.current?.querySelector<HTMLUListElement>(".v3-select-list");
    const active = list?.children[activeIndex] as HTMLElement | undefined;
    active?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        setActiveIndex(Math.max(0, options.findIndex((option) => option.value === value)));
        setOpen(true);
      }
      return;
    }
    if (event.key === "Escape") { event.preventDefault(); setOpen(false); return; }
    if (event.key === "ArrowDown") { event.preventDefault(); setActiveIndex((index) => Math.min(index + 1, options.length - 1)); return; }
    if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex((index) => Math.max(index - 1, 0)); return; }
    if (event.key === "Home") { event.preventDefault(); setActiveIndex(0); return; }
    if (event.key === "End") { event.preventDefault(); setActiveIndex(options.length - 1); return; }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const option = options[activeIndex];
      if (option && !option.disabled) { onChange(option.value); setOpen(false); }
      return;
    }
    if (event.key === "Tab") setOpen(false);
  }

  return <div className={`v3-select ${open ? "open" : ""} ${disabled ? "disabled" : ""}`} ref={rootRef} onKeyDown={onKeyDown}>
    <button
      type="button"
      className="v3-select-trigger"
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={ariaLabel}
      aria-controls={listId}
      onClick={() => { if (!disabled) { setActiveIndex(Math.max(0, options.findIndex((option) => option.value === value))); setOpen(!open); } }}
    >
      <span className={`v3-select-value ${selected ? "" : "placeholder"}`}>{selected?.label ?? placeholder}</span>
      <ChevronDown size={16} aria-hidden />
    </button>
    {open && <ul className="v3-select-list" id={listId} role="listbox" aria-label={ariaLabel}>
      {options.map((option, index) => <li
        key={option.value}
        role="option"
        aria-selected={option.value === value}
        className={`v3-select-option ${index === activeIndex ? "active" : ""} ${option.value === value ? "selected" : ""} ${option.disabled ? "disabled" : ""}`}
        onMouseEnter={() => setActiveIndex(index)}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => { if (!option.disabled) { onChange(option.value); setOpen(false); } }}
      >
        <span className="v3-select-option-label">
          <span>{option.label}</span>
          {option.hint && <small>{option.hint}</small>}
        </span>
        {option.value === value && <Check size={15} aria-hidden />}
      </li>)}
      {options.length === 0 && <li className="v3-select-empty">暂无可选项</li>}
    </ul>}
  </div>;
}
